import { apiRequest } from './client'
import { readInt, readString } from './json'

export type CatalogSummaryDto = {
  totalItems: number
  matchAuto: number
  matchSuggested: number
  matchManual: number
  matchUnmatched: number
  needsAttention: number
  staleItems: number
  hiddenFromSearch: number
}

export type CatalogFreshnessStatus = 'fresh' | 'stale_7' | 'stale_14' | 'stale_30'

export type SupplierCatalogItemDto = {
  id: number
  organizationId: number
  supplierName: string
  matchStatus: string
  externalSku: string | null
  brand: string | null
  country: string | null
  unit: string | null
  price: number | null
  minShipmentQty: number | null
  packageSize: string | null
  productTypeId: number | null
  productTypeCode: string | null
  productTypeName: string | null
  productTypeActive: boolean | null
  matchConfidence: number | null
  operatorComment: string | null
  updatedAt: string | null
  freshnessStatus: CatalogFreshnessStatus | null
  daysSinceUpdate: number | null
  buyerSearchVisible: boolean | null
  privateSupplierId: number | null
  privateSupplierName: string | null
  /** Previous list price from history (buyer my-prices enrichment). */
  previousPrice: number | null
  priceDeltaPct: number | null
  previousObservedAt: string | null
  catalogListIds?: number[]
}

export type CatalogItemsPageDto = {
  items: SupplierCatalogItemDto[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

/** Поля таблицы, по которым API разрешает сортировку. Не передаём в запрос произвольные имена колонок. */
export const CATALOG_ITEM_SORT_KEYS = [
  'name',
  'sku',
  'price',
  'unit',
  'package',
  'minShipment',
  'brand',
  'country',
  'category',
  'updated',
  'match',
  'supplier',
] as const

export type CatalogItemSortKey = (typeof CATALOG_ITEM_SORT_KEYS)[number]
export type CatalogItemSortDirection = 'asc' | 'desc'

export type SupplierCatalogFilterOptionsDto = {
  brands: string[]
  countries: string[]
}

type Raw = Record<string, unknown>

const normalizeSummary = (json: Raw): CatalogSummaryDto => ({
  totalItems: Number(json.total_items ?? json.totalItems ?? 0),
  matchAuto: Number(json.match_auto ?? json.matchAuto ?? 0),
  matchSuggested: Number(json.match_suggested ?? json.matchSuggested ?? 0),
  matchManual: Number(json.match_manual ?? json.matchManual ?? 0),
  matchUnmatched: Number(json.match_unmatched ?? json.matchUnmatched ?? 0),
  needsAttention: Number(json.needs_attention ?? json.needsAttention ?? 0),
  staleItems: Number(json.stale_items ?? json.staleItems ?? 0),
  hiddenFromSearch: Number(json.hidden_from_search ?? json.hiddenFromSearch ?? 0),
})

const normalizeItem = (json: Raw): SupplierCatalogItemDto => ({
  id: readInt(json, 'id', 'id'),
  organizationId: readInt(json, 'organizationId', 'organization_id'),
  supplierName: readString(json, 'supplierName', 'supplier_name') ?? '',
  matchStatus: readString(json, 'matchStatus', 'match_status') ?? '',
  externalSku: readString(json, 'externalSku', 'external_sku'),
  brand: (json.brand as string | null) ?? null,
  country: (json.country as string | null) ?? null,
  unit: (json.unit as string | null) ?? null,
  price: json.price != null ? Number(json.price) : null,
  minShipmentQty:
    json.minShipmentQty != null
      ? Number(json.minShipmentQty)
      : json.min_shipment_qty != null
        ? Number(json.min_shipment_qty)
        : null,
  packageSize: readString(json, 'packageSize', 'package_size'),
  productTypeId:
    json.productTypeId != null
      ? Number(json.productTypeId)
      : json.product_type_id != null
        ? Number(json.product_type_id)
        : null,
  productTypeCode: readString(json, 'productTypeCode', 'product_type_code'),
  productTypeName: readString(json, 'productTypeName', 'product_type_name'),
  productTypeActive:
    json.productTypeActive != null
      ? Boolean(json.productTypeActive)
      : json.product_type_active != null
        ? Boolean(json.product_type_active)
        : null,
  matchConfidence:
    json.matchConfidence != null
      ? Number(json.matchConfidence)
      : json.match_confidence != null
        ? Number(json.match_confidence)
        : null,
  operatorComment: readString(json, 'operatorComment', 'operator_comment'),
  updatedAt: readString(json, 'updatedAt', 'updated_at'),
  freshnessStatus: (() => {
    const raw = readString(json, 'freshnessStatus', 'freshness_status')
    if (raw === 'fresh' || raw === 'stale_7' || raw === 'stale_14' || raw === 'stale_30') {
      return raw
    }
    return null
  })(),
  daysSinceUpdate:
    json.daysSinceUpdate != null
      ? Number(json.daysSinceUpdate)
      : json.days_since_update != null
        ? Number(json.days_since_update)
        : null,
  buyerSearchVisible:
    json.buyerSearchVisible != null
      ? Boolean(json.buyerSearchVisible)
      : json.buyer_search_visible != null
        ? Boolean(json.buyer_search_visible)
        : null,
  privateSupplierId:
    json.privateSupplierId != null
      ? Number(json.privateSupplierId)
      : json.private_supplier_id != null
        ? Number(json.private_supplier_id)
        : null,
  privateSupplierName: readString(json, 'privateSupplierName', 'private_supplier_name'),
  previousPrice:
    json.previousPrice != null
      ? Number(json.previousPrice)
      : json.previous_price != null
        ? Number(json.previous_price)
        : null,
  priceDeltaPct:
    json.priceDeltaPct != null
      ? Number(json.priceDeltaPct)
      : json.price_delta_pct != null
        ? Number(json.price_delta_pct)
        : null,
  previousObservedAt: readString(json, 'previousObservedAt', 'previous_observed_at'),
  catalogListIds: ((json.catalogListIds as unknown[] | undefined) ?? (json.catalog_list_ids as unknown[] | undefined) ?? [])
    .map(Number)
    .filter(Number.isFinite),
})

const normalizePage = (json: Raw): CatalogItemsPageDto => ({
  items: ((json.items as Raw[] | undefined) ?? []).map(normalizeItem),
  page: Number(json.page ?? 0),
  size: Number(json.size ?? 0),
  totalElements: Number(json.totalElements ?? json.total_elements ?? 0),
  totalPages: Number(json.totalPages ?? json.total_pages ?? 0),
})

export const getCatalogSummary = async (organizationId: number): Promise<CatalogSummaryDto> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/summary?organizationId=${organizationId}`,
  )
  return normalizeSummary(result)
}

export const listSupplierCatalogFilterOptions = async (
  organizationId: number,
): Promise<SupplierCatalogFilterOptionsDto> => {
  const response = await apiRequest<Raw>(
    `/supplier/catalog/filter-options?organizationId=${organizationId}`,
  )
  const brands = (response.brands as string[] | undefined) ?? []
  const countries = (response.countries as string[] | undefined) ?? []
  return { brands, countries }
}

export const listSupplierCatalogItems = async (options: {
  organizationId: number
  page?: number
  size?: number
  matchStatus?: string
  productTypeId?: number
  unassignedProductType?: boolean
  search?: string
  sortBy?: CatalogItemSortKey
  sortDirection?: CatalogItemSortDirection
}): Promise<CatalogItemsPageDto> => {
  const params = new URLSearchParams({
    organizationId: String(options.organizationId),
    page: String(options.page ?? 0),
    size: String(options.size ?? 50),
  })
  if (options.matchStatus) {
    params.set('matchStatus', options.matchStatus)
  }
  if (options.productTypeId != null) {
    params.set('productTypeId', String(options.productTypeId))
  }
  if (options.unassignedProductType) {
    params.set('unassignedProductType', 'true')
  }
  if (options.search?.trim()) {
    params.set('search', options.search.trim())
  }
  if (options.sortBy) {
    params.set('sortBy', options.sortBy)
    params.set('sortDirection', options.sortDirection ?? 'asc')
  }
  const result = await apiRequest<Raw>(`/supplier/catalog/items?${params.toString()}`)
  return normalizePage(result)
}

export const createCatalogItem = async (options: {
  organizationId: number
  name: string
  brand?: string | null
  externalSku?: string | null
}): Promise<SupplierCatalogItemDto> => {
  const result = await apiRequest<Raw>('/supplier/catalog/items', {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      name: options.name.trim(),
      brand: options.brand?.trim() || undefined,
      external_sku: options.externalSku?.trim() || undefined,
    },
  })
  return normalizeItem(result)
}

export type CatalogItemFieldsUpdate = {
  name?: string | null
  brand?: string | null
  price?: number | null
  country?: string | null
  unit?: string | null
  minShipmentQty?: number | null
  packageSize?: string | null
  comment?: string | null
}

export const updateCatalogItemFields = async (
  itemId: number,
  update: CatalogItemFieldsUpdate,
): Promise<SupplierCatalogItemDto> => {
  const body: Record<string, unknown> = {}
  if (update.name !== undefined) body.name = update.name
  if (update.brand !== undefined) body.brand = update.brand
  if (update.price !== undefined) body.price = update.price
  if (update.country !== undefined) body.country = update.country
  if (update.unit !== undefined) body.unit = update.unit
  if (update.minShipmentQty !== undefined) body.min_shipment_qty = update.minShipmentQty
  if (update.packageSize !== undefined) body.package_size = update.packageSize
  if (update.comment !== undefined) body.comment = update.comment

  const result = await apiRequest<Raw>(`/supplier/catalog/items/${itemId}/fields`, {
    method: 'PATCH',
    body,
  })
  return normalizeItem(result)
}

export const assignCatalogProductType = async (
  itemId: number,
  productTypeId: number,
): Promise<SupplierCatalogItemDto> => {
  const result = await apiRequest<Raw>(`/supplier/catalog/items/${itemId}/product-type`, {
    method: 'PATCH',
    body: { product_type_id: productTypeId },
  })
  return normalizeItem(result)
}

export const confirmCatalogSuggestedMatch = async (itemId: number): Promise<SupplierCatalogItemDto> => {
  const result = await apiRequest<Raw>(`/supplier/catalog/items/${itemId}/confirm-suggestion`, {
    method: 'POST',
  })
  return normalizeItem(result)
}

export const deleteCatalogItem = async (itemId: number): Promise<number> => {
  const result = await apiRequest<Raw>(`/supplier/catalog/items/${itemId}`, {
    method: 'DELETE',
  })
  return Number(result.deleted ?? 0)
}

export const bulkDeleteCatalogItems = async (options: {
  organizationId: number
  itemIds?: number[]
  deleteAll?: boolean
}): Promise<number> => {
  const result = await apiRequest<Raw>('/supplier/catalog/items/bulk-delete', {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      ...(options.itemIds?.length ? { item_ids: options.itemIds } : {}),
      delete_all: options.deleteAll ?? false,
    },
  })
  return Number(result.deleted ?? 0)
}

export type SupplierCatalogRematchResult = {
  organizationId: number
  rematchedCount: number
  skippedManualCount: number
  matcherVersion: string
}

export const rematchSupplierCatalog = async (
  organizationId: number,
): Promise<SupplierCatalogRematchResult> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/rematch?organizationId=${organizationId}&preserveManual=true`,
    { method: 'POST' },
  )
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    rematchedCount: Number(result.rematchedCount ?? result.rematched_count ?? 0),
    skippedManualCount: Number(result.skippedManualCount ?? result.skipped_manual_count ?? 0),
    matcherVersion: readString(result, 'matcherVersion', 'matcher_version') ?? '',
  }
}

export type CatalogBrandBackfillResult = {
  organizationId: number
  scannedBlank: number
  filled: number
  skippedManual: number
}

export const backfillCatalogBrands = async (
  organizationId: number,
): Promise<CatalogBrandBackfillResult> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/backfill-brands?organizationId=${organizationId}`,
    { method: 'POST' },
  )
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    scannedBlank: Number(result.scannedBlank ?? result.scanned_blank ?? 0),
    filled: Number(result.filled ?? 0),
    skippedManual: Number(result.skippedManual ?? result.skipped_manual ?? 0),
  }
}

export type CatalogBrandNormalizeResult = {
  organizationId: number
  scanned: number
  cleared: number
  canonicalized: number
  protectedFromReimport: number
}

export const normalizeCatalogBrands = async (
  organizationId: number,
): Promise<CatalogBrandNormalizeResult> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/normalize-brands?organizationId=${organizationId}`,
    { method: 'POST' },
  )
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    scanned: Number(result.scanned ?? 0),
    cleared: Number(result.cleared ?? 0),
    canonicalized: Number(result.canonicalized ?? 0),
    protectedFromReimport: Number(
      result.protectedFromReimport ?? result.protected_from_reimport ?? 0,
    ),
  }
}

export type CatalogDuplicateMatchReason = 'EXTERNAL_SKU' | 'IMPORT_SOURCE_NAME' | 'NAME_SIMILAR'

export type CatalogDuplicateGroupDto = {
  matchReason: CatalogDuplicateMatchReason
  matchKey: string
  items: SupplierCatalogItemDto[]
}

export type CatalogDuplicateGroupsResponse = {
  organizationId: number
  groups: CatalogDuplicateGroupDto[]
}

export type MergeCatalogDuplicatesResult = {
  keptItem: SupplierCatalogItemDto
  mergedCount: number
}

const normalizeDuplicateReason = (raw: string | null): CatalogDuplicateMatchReason => {
  if (raw === 'EXTERNAL_SKU' || raw === 'IMPORT_SOURCE_NAME' || raw === 'NAME_SIMILAR') {
    return raw
  }
  return 'NAME_SIMILAR'
}

export const listCatalogDuplicates = async (
  organizationId: number,
): Promise<CatalogDuplicateGroupsResponse> => {
  const result = await apiRequest<Raw>(
    `/supplier/catalog/duplicates?organizationId=${organizationId}`,
  )
  const groups = ((result.groups as Raw[] | undefined) ?? []).map((group) => ({
    matchReason: normalizeDuplicateReason(
      readString(group, 'matchReason', 'match_reason'),
    ),
    matchKey: readString(group, 'matchKey', 'match_key') ?? '',
    items: ((group.items as Raw[] | undefined) ?? []).map(normalizeItem),
  }))
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    groups,
  }
}

export type DismissCatalogDuplicatesResult = {
  organizationId: number
  dismissedPairs: number
  alreadyDismissedPairs: number
}

/** «Это не дубликаты» — решение организации: развести позиции группы как разные товары. */
export const dismissCatalogDuplicates = async (options: {
  organizationId: number
  itemIds: number[]
}): Promise<DismissCatalogDuplicatesResult> => {
  const result = await apiRequest<Raw>('/supplier/catalog/duplicates/dismiss', {
    method: 'POST',
    body: {
      organization_id: options.organizationId,
      item_ids: options.itemIds,
    },
  })
  return {
    organizationId: readInt(result, 'organizationId', 'organization_id'),
    dismissedPairs: Number(result.dismissedPairs ?? result.dismissed_pairs ?? 0),
    alreadyDismissedPairs: Number(
      result.alreadyDismissedPairs ?? result.already_dismissed_pairs ?? 0,
    ),
  }
}

export const mergeCatalogDuplicates = async (options: {
  organizationId: number
  keepItemId: number
  mergeItemIds: number[]
  name?: string | null
  brand?: string | null
  price?: number | null
  country?: string | null
  unit?: string | null
  externalSku?: string | null
}): Promise<MergeCatalogDuplicatesResult> => {
  const body: Record<string, unknown> = {
    organization_id: options.organizationId,
    keep_item_id: options.keepItemId,
    merge_item_ids: options.mergeItemIds,
  }
  if (options.name !== undefined) body.name = options.name
  if (options.brand !== undefined) body.brand = options.brand
  if (options.price !== undefined) body.price = options.price
  if (options.country !== undefined) body.country = options.country
  if (options.unit !== undefined) body.unit = options.unit
  if (options.externalSku !== undefined) body.external_sku = options.externalSku

  const result = await apiRequest<Raw>('/supplier/catalog/duplicates/merge', {
    method: 'POST',
    body,
  })
  const keptRaw = (result.kept_item as Raw | undefined) ?? (result.keptItem as Raw | undefined) ?? {}
  return {
    keptItem: normalizeItem(keptRaw),
    mergedCount: Number(result.merged_count ?? result.mergedCount ?? 0),
  }
}
